import type { CheckResult, CDNCheckResultData, WAFCheckResultData } from '../api/diagnosticsReportApi';
import { type CheckRow, getBlockedBots, getFailureReason } from '../diagnosticsReportUtils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type DocsLink = {
  label: string;
  url: string;
};

// A step is either plain text, or text with a multi-line code snippet rendered
// directly beneath it (e.g. the robots.txt Allow-rule example).
export type FixStep = string | { text: string; codeBlock?: string };

export type ResolvedFixStep = {
  text: string;
  codeBlock?: string;
};

export type FixTip = {
  id: string;
  description: string;
  steps: FixStep[];
  docsLinks?: DocsLink[];
};

export type ResolvedFixTip = {
  id: string;
  description: string;
  steps: ResolvedFixStep[];
  docsLinks?: DocsLink[];
  // Caveat shown under the remediation steps when the "blocked" verdict may be a
  // false positive. Absent for checks the caveat doesn't apply to. (PRE-3100)
  notice?: string;
};

// ---------------------------------------------------------------------------
// Type guards
// ---------------------------------------------------------------------------

export function isCDNResult(r: CheckResult): r is CDNCheckResultData {
  return typeof r === 'object' && 'cdnProvider' in r;
}

export function isWAFResult(r: CheckResult): r is WAFCheckResultData {
  return typeof r === 'object' && 'hardBlocks' in r;
}

// ---------------------------------------------------------------------------
// Matcher helpers — inspect rawResults for specific signals
// ---------------------------------------------------------------------------

function anyCDNResult(rawResults: Record<string, CheckResult>): CDNCheckResultData | undefined {
  return Object.values(rawResults).find(isCDNResult);
}

function anyWAFResult(rawResults: Record<string, CheckResult>): WAFCheckResultData | undefined {
  return Object.values(rawResults).find(isWAFResult);
}

function hasSoftBlock(rawResults: Record<string, CheckResult>, type: string): boolean {
  return Object.values(rawResults).some(
    (r) => isCDNResult(r) && r.softBlocksDetected.includes(type),
  );
}

function hasBotManagementSignals(rawResults: Record<string, CheckResult>): boolean {
  return Object.values(rawResults).some(
    (r) => isCDNResult(r) && r.botManagementSignals.length > 0,
  );
}

function hasHardBlock(rawResults: Record<string, CheckResult>, code: string): boolean {
  return Object.values(rawResults).some(
    (r) => isWAFResult(r) && r.hardBlocks.includes(code),
  );
}

function hasAnySoftBlock(rawResults: Record<string, CheckResult>): boolean {
  return Object.values(rawResults).some(
    (r) => isCDNResult(r) && r.softBlocksDetected.length > 0,
  );
}

function hasAISpecificBlock(rawResults: Record<string, CheckResult>): boolean {
  return Object.values(rawResults).some(
    (r) => isCDNResult(r) && r.aiSpecificBlock,
  );
}

// ---------------------------------------------------------------------------
// Tip matchers — ordered by specificity (first match wins)
// ---------------------------------------------------------------------------

type FixTipMatcher = {
  checkKey: string;
  match: (rawResults: Record<string, CheckResult>) => boolean;
  tip: FixTip;
  // Most tips remediate a confirmed block, and a check that never produced an answer has
  // no block to remediate. A tip opts in here to address the failure itself instead —
  // the cause is actionable even though the crawler verdict is unknown.
  appliesToFailedCheck?: boolean;
};

const FIX_TIP_MATCHERS: FixTipMatcher[] = [
  // --- Integration tip ---
  {
    checkKey: 'Integration',
    match: () => true,
    tip: {
      id: 'integration-not-serving',
      description:
        'Prerender is not serving cached pages to {blockedCrawlers}. Add {plural:this AI bot|these AI bots} to your Prerender integration code so {plural:its|their} requests are routed through Prerender.',
      steps: [
        // Lowercase on purpose: several official integration samples lowercase the
        // incoming user agent and compare the configured tokens verbatim, so a
        // mixed-case token (e.g. "Google-Extended") silently never matches there.
        // Lowercase tokens work in every integration sample.
        'Check your Prerender integration code and ensure {blockedBotsLowercase} user {plural:agent is|agents are} listed.',
        'Re-run diagnostics.',
      ],
      docsLinks: [
        {
          label: 'How to add AI crawlers and bots to your integration',
          url: 'https://docs.prerender.io/docs/how-to-add-additional-bots',
        },
      ],
    },
  },

  // --- CDN tips (most specific first) ---
  {
    checkKey: 'CDN',
    match: (raw) => hasSoftBlock(raw, 'cloudflare') && hasBotManagementSignals(raw),
    tip: {
      id: 'cdn-cloudflare-bot-mgmt',
      description:
        'Cloudflare Bot Management is blocking {blockedCrawlers}.',
      steps: [
        'In your Cloudflare dashboard, go to Security > Settings and filter by Bot traffic.',
        'Under Super Bot Fight Mode, set Verified bots to Allow.',
        'Go to Security > WAF > Custom Rules and create a rule with the Skip action to bypass Super Bot Fight Mode for {blockedBots} user-agents.',
        'Re-run diagnostics.',
      ],
    },
  },
  {
    checkKey: 'CDN',
    match: (raw) => hasSoftBlock(raw, 'cloudflare'),
    tip: {
      id: 'cdn-cloudflare-soft-block',
      description:
        'Your CDN (Cloudflare) is challenging or blocking {blockedCrawlers}.',
      steps: [
        'In your Cloudflare dashboard, go to AI Crawl Control and check if {blockedBots} are set to Allow. If not, update to Allow for each affected crawler.',
        'Go to Security > Settings, filter by Bot traffic and check if Block AI Bots is enabled. If so, disable it to allow AI crawlers through.',
        'Check if Bot Fight Mode is enabled under the same settings: on a free plan, Bot Fight Mode cannot be bypassed — you will need to disable it entirely or upgrade to a Pro plan to create exceptions for specific crawlers. On a Pro plan or above, you will have Super Bot Fight Mode — create a WAF custom rule with the Skip action to allow {blockedBots} through.',
        'Go to Security > WAF > Custom Rules and check for any rules challenging or blocking {blockedBots} user-agents. Add an exception or remove the rule.',
        'Re-run diagnostics.',
      ],
    },
  },
  {
    checkKey: 'CDN',
    match: (raw) => hasSoftBlock(raw, 'datadome'),
    tip: {
      id: 'cdn-datadome',
      description: 'DataDome bot protection is blocking {blockedCrawlers}.',
      steps: [
        'In your DataDome dashboard, go to Custom Rules.',
        'Create a rule to allow {blockedBots} user-agents.',
        'Re-run diagnostics.',
      ],
    },
  },
  {
    checkKey: 'CDN',
    match: (raw) => hasSoftBlock(raw, 'imperva'),
    tip: {
      id: 'cdn-imperva',
      description: 'Imperva/Incapsula is blocking {blockedCrawlers}.',
      steps: [
        'In your Imperva Cloud WAF dashboard, open your site\'s security policy.',
        'Create an exception rule to allow {blockedBots} user-agents.',
        'Re-run diagnostics.',
      ],
    },
  },
  {
    checkKey: 'CDN',
    match: (raw) => hasSoftBlock(raw, 'perimeterx'),
    tip: {
      id: 'cdn-perimeterx',
      description:
        'PerimeterX (HUMAN Security) is blocking {blockedCrawlers}.',
      steps: [
        'In your HUMAN Bot Defender console, go to Policy Rules.',
        'Under Known bots & Crawlers, set {blockedBots} to "allow".',
        'Alternatively, create a custom policy rule for these user-agents.',
        'Re-run diagnostics.',
      ],
    },
  },
  {
    checkKey: 'CDN',
    match: (raw) => hasSoftBlock(raw, 'sucuri'),
    tip: {
      id: 'cdn-sucuri',
      description: 'Sucuri WAF is blocking {blockedCrawlers}.',
      steps: [
        'Sucuri does not support user-agent allow lists. Whitelist the crawler IP addresses in Firewall > Access Control instead.',
        'Alternatively, check Security Options and disable the "Aggressive Bot Filter" if enabled.',
        'Re-run diagnostics.',
      ],
    },
  },
  {
    checkKey: 'CDN',
    match: (raw) => hasAISpecificBlock(raw) && !hasAnySoftBlock(raw),
    tip: {
      id: 'cdn-ai-specific-block',
      description:
        'Your infrastructure is selectively blocking AI crawlers while allowing Googlebot.',
      steps: [
        'Check your server or CDN configuration for user-agent-based rules.',
        'Look for rules that block non-Google bots.',
        'Add exceptions for {blockedBots} user-agents.',
        'Re-run diagnostics.',
      ],
    },
  },
  {
    checkKey: 'CDN',
    match: () => true,
    tip: {
      id: 'cdn-generic',
      description:
        'Your CDN or hosting provider is blocking {blockedCrawlers}.',
      steps: [
        'Check your CDN or hosting provider\'s security settings.',
        'Look for bot protection or firewall rules that may block AI crawlers.',
        'Add exceptions for {blockedBots} user-agents.',
        'Re-run diagnostics.',
      ],
    },
  },

  // --- WAF tips ---
  {
    checkKey: 'WAF',
    match: (raw) => hasHardBlock(raw, '403'),
    tip: {
      id: 'waf-403',
      description:
        'Your WAF is returning 403 Forbidden to {blockedCrawlers}.',
      steps: [
        'Review your WAF rules for any that are blocking {blockedBots} user-agents.',
        'Remove the block rule, or create an explicit allow rule for {blockedBots} user-agents.',
        'If you created an allow rule, ensure it has higher priority than any existing block rules.',
        'Re-run diagnostics.',
      ],
    },
  },
  {
    checkKey: 'WAF',
    match: (raw) => hasHardBlock(raw, '429'),
    tip: {
      id: 'waf-429',
      description:
        'Your WAF is rate-limiting {blockedCrawlers} (HTTP 429).',
      steps: [
        'Review your WAF rate-limiting rules.',
        'Increase rate limits or create exceptions for {blockedBots} user-agents.',
        'Re-run diagnostics.',
      ],
    },
  },
  {
    checkKey: 'WAF',
    match: (raw) => hasHardBlock(raw, '503'),
    tip: {
      id: 'waf-503',
      description:
        'Your WAF is returning 503 Service Unavailable to {blockedCrawlers}.',
      steps: [
        'Review your WAF settings and check if an enhanced security or attack protection mode is enabled.',
        'Create bypass rules for {blockedBots} user-agents.',
        'Re-run diagnostics.',
      ],
    },
  },
  {
    checkKey: 'WAF',
    match: () => true,
    tip: {
      id: 'waf-generic',
      description: 'Your firewall is blocking {blockedCrawlers} requests.',
      steps: [
        'Review your WAF or firewall configuration for any block rules affecting {blockedBots} user-agents.',
        'Add {blockedBots} user-agents to the allow list.',
        'Re-run diagnostics.',
      ],
    },
  },

  // --- Robots.txt ---
  {
    checkKey: 'robots.txt',
    match: () => true,
    tip: {
      id: 'robots-disallow',
      description:
        'Your robots.txt file is disallowing {blockedCrawlers} from accessing this page.',
      steps: [
        'Open your robots.txt file. It is typically found at `yourdomain/robots.txt`.',
        'Look for Disallow rules targeting {blockedBots} user-agents (e.g., `User-agent: {firstBlockedBot}` followed by `Disallow: /`).',
        'Remove Disallow rules specific to the blocked crawlers to allow access.',
        {
          text: 'If you have a wildcard `User-agent: *` block with `Disallow: /`, add an explicit `Allow` rule for each blocked crawler:',
          codeBlock: 'User-agent: *\nDisallow: /\n\nUser-agent: {firstBlockedBot}\nAllow: /',
        },
        'Re-run diagnostics.',
      ],
    },
  },

  // --- Robots.txt could not be read ---
  // Neither tip names blocked crawlers: the check never established a verdict, so the
  // remediation is to make the file readable and run again.
  {
    checkKey: 'robots.txt',
    appliesToFailedCheck: true,
    match: (rawResults) => getFailureReason(rawResults) === 'robots-server-error',
    tip: {
      id: 'robots-server-error',
      description:
        "We couldn't open your robots.txt file because your server returned an error. Until it loads, we can't tell which crawlers your site allows.",
      steps: [
        'Open `yourdomain/robots.txt` in your browser and check whether it loads or shows an error page.',
        'If you see an error page, your server is having trouble serving the file. Many crawlers read an error on robots.txt as a reason to skip your whole site.',
        'Check whether the rest of your site is loading. If other pages also fail, the issue is your server or hosting rather than robots.txt itself. Look at your hosting dashboard or status page for an outage, and your server error logs for the cause.',
        'Once the file opens reliably, rerun diagnostics.',
      ],
    },
  },
  {
    checkKey: 'robots.txt',
    appliesToFailedCheck: true,
    match: (rawResults) => getFailureReason(rawResults) === 'robots-rate-limited',
    tip: {
      id: 'robots-rate-limited',
      description:
        "We couldn't open your robots.txt file because your site limited how often it can be requested. Crawlers may still reach it, but we couldn't confirm what your rules allow.",
      steps: [
        'Open `yourdomain/robots.txt` in your browser and check that the file loads.',
        "Crawlers may still crawl in this case, but we couldn't read your rules to confirm what they allow.",
        'Look for a rate limit or "too many requests" rule in your security or CDN settings, or in a security plugin if your site uses one, and allow robots.txt to be requested freely so it can always be read.',
        'Rerun diagnostics.',
      ],
    },
  },
  {
    checkKey: 'robots.txt',
    appliesToFailedCheck: true,
    match: (rawResults) => getFailureReason(rawResults) === 'robots-forbidden',
    tip: {
      id: 'robots-forbidden',
      description:
        "Your site blocked our request for robots.txt, so we couldn't read your crawler rules. This often means a firewall or access rule is blocking the file.",
      steps: [
        'Open `yourdomain/robots.txt` in your browser. If it loads for you but not for us, something is blocking visitors from outside your network.',
        'Look through your security, firewall, or CDN settings (or a security plugin if your site uses one) for any rule that blocks robots.txt or blocks visitors based on where they come from.',
        'Allow anyone to open robots.txt. Crawlers have to read it before they can visit any page.',
        'Rerun diagnostics.',
      ],
    },
  },
  {
    checkKey: 'robots.txt',
    appliesToFailedCheck: true,
    match: (rawResults) => getFailureReason(rawResults) === 'robots-unreadable',
    tip: {
      id: 'robots-unreadable',
      description:
        "Instead of your robots.txt file, your site showed us a \"prove you're human\" security check, so we couldn't read your crawler rules.",
      steps: [
        'Open `yourdomain/robots.txt` in your browser and check that you get the file itself, not a security or "are you human" page.',
        'Find the bot protection or security tool that serves that challenge (often in your CDN or security settings) and add a rule that lets requests for robots.txt through without a challenge.',
        'Crawlers have to read this file before they can visit any page, so it is safe to let it through.',
        'Rerun diagnostics.',
      ],
    },
  },
];

// ---------------------------------------------------------------------------
// Crawler-mimicking notice
// ---------------------------------------------------------------------------

// Our probe reaches the site by sending the crawler's user-agent from our own IPs, so a
// "blocked" verdict on these checks may be a false positive. Two different reasons, so
// two wordings (PO Venessa, PRE-3100):
//
//   - CDN / WAF: the tool verifies the crawler by IP as well as user-agent, and admits
//     the real crawler after a check our probe can't reproduce.
//   - Integration: a CDN or WAF rejects our probe outright, so it never reaches the
//     origin and the row reads "not serving" for an integration that is fine.
//
// Opt-in per check rather than excluding robots.txt, so a future check has to state that
// a caveat applies to it. robots.txt is read directly, with a browser user-agent, and
// needs no caveat.
const VERIFICATION_NOTICE =
  'We tested access by mimicking the crawler. Some tools use additional verification ' +
  'checks to let crawlers through that our test can’t replicate. Check your ' +
  '{checkName} to confirm that {crawlers} {verb} allowed.';

const NOTICE_TEMPLATES: Record<string, string> = {
  Integration:
    'We tested access by mimicking the crawler. A firewall or CDN may block our request ' +
    'even when real crawlers get through. Check your Prerender integration to confirm ' +
    'that {crawlers} {verb} routed through Prerender.',
  CDN: VERIFICATION_NOTICE,
  WAF: VERIFICATION_NOTICE,
};

// "A", "A and B", "A, B and C" — the notice reads as a sentence, so the bare
// comma-join the fix steps use would produce "that A, B is allowed".
function formatProviderList(providers: string[]): string {
  if (providers.length <= 1) return providers[0] ?? '';

  return `${providers.slice(0, -1).join(', ')} and ${providers[providers.length - 1]}`;
}

function buildNotice(row: CheckRow): string | undefined {
  const template = NOTICE_TEMPLATES[row.checkKey];
  if (!template) return undefined;

  return template
    .replace('{checkName}', row.displayName)
    .replace('{crawlers}', formatProviderList(row.blockedProviders))
    .replace('{verb}', row.blockedProviders.length === 1 ? 'is' : 'are');
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function findFixTip(checkRow: CheckRow): FixTip | null {
  if (checkRow.failed) {
    const failureMatcher = FIX_TIP_MATCHERS.find(
      (m) => m.checkKey === checkRow.checkKey && m.appliesToFailedCheck && m.match(checkRow.rawResults),
    );

    return failureMatcher?.tip ?? null;
  }

  if (checkRow.blockedProviders.length === 0) return null;

  const matcher = FIX_TIP_MATCHERS.find(
    (m) => m.checkKey === checkRow.checkKey && !m.appliesToFailedCheck && m.match(checkRow.rawResults),
  );

  return matcher?.tip ?? null;
}

// {plural:one|many} resolves to its first variant when the tip addresses a single
// blocked crawler and to its second otherwise, so copy agrees with the count.
function resolvePlaceholders(template: string, blockedCrawlers: string, blockedBots: string, firstBlockedBot: string, blockedCount: number): string {
  return template
    .replace(/\{blockedCrawlers\}/g, blockedCrawlers)
    .replace(/\{blockedBots\}/g, blockedBots)
    .replace(/\{blockedBotsLowercase\}/g, blockedBots.toLowerCase())
    .replace(/\{firstBlockedBot\}/g, firstBlockedBot)
    .replace(/\{plural:([^|}]*)\|([^}]*)\}/g, (_, singular: string, plural: string) => (blockedCount === 1 ? singular : plural));
}

export function resolveFixTip(tip: FixTip, row: CheckRow): ResolvedFixTip {
  const blockedProviders = row.blockedProviders;
  const blockedBots = getBlockedBots(row.rawResults);

  const crawlerList = blockedProviders.join(', ');
  const botList = blockedBots.length > 0 ? blockedBots.join(', ') : crawlerList;
  const firstBot = blockedBots[0] ?? blockedProviders[0] ?? '';
  const blockedCount = blockedBots.length > 0 ? blockedBots.length : blockedProviders.length;

  const resolveStep = (step: FixStep): ResolvedFixStep => {
    const text = typeof step === 'string' ? step : step.text;
    const codeBlock = typeof step === 'string' ? undefined : step.codeBlock;

    return {
      text: resolvePlaceholders(text, crawlerList, botList, firstBot, blockedCount),
      codeBlock: codeBlock ? resolvePlaceholders(codeBlock, crawlerList, botList, firstBot, blockedCount) : undefined,
    };
  };

  return {
    id: tip.id,
    description: resolvePlaceholders(tip.description, crawlerList, botList, firstBot, blockedCount),
    steps: tip.steps.map(resolveStep),
    docsLinks: tip.docsLinks,
    notice: buildNotice(row),
  };
}
